import {Component, Inject, OnInit} from '@angular/core';
import {PrintDocument} from '@application/command/print-document';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {CrudActions} from '@application/helper/grid-action-buttons-helper/crud-actions.interface';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {RemoveConfirmationService} from '@application/helper/remove-confirmation/remove-confirmation.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {BuggyForProductionSchedule} from '@domain/production-schedule/buggy-for-production-schedule';
import {Permission} from '@domain/profile/permission.enum';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {HttpBuggyService} from '@infrastructure/http/buggy/http-buggy.service';
import {
  AssertionUtils,
  BaseComponent,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  GridButtonClickedEvent,
  GridOptionsBuilder,
  NavigationHelperPrototypeService,
  TranslateService
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {finalize, forkJoin, takeUntil} from 'rxjs';
import {BuggyCustomSettingsComponent} from '../custom-settings/buggy-custom-settings.component';
import {BuggyOverviewService} from './buggy-overview.service';

@Component({
  templateUrl: './buggy-overview-page.component.html'
})
export class BuggyOverviewPageComponent extends BaseComponent implements OnInit {
  public config: CrudOverviewDataConfig<BuggyForProductionSchedule>;
  private readonly pageUrls = LastModifiedCardUtils.getPageUrls('buggy');
  private readonly printBuggyOverviewPermission: Permission = Permission.PRINT_BUGGY_OVERVIEW;
  private hasPrintBuggyOverviewPermission: boolean;
  private isPrintingBuggyReport: boolean = false;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly crudOverviewDataBuilderFactory: CrudOverviewDataBuilderFactoryService<BuggyForProductionSchedule>,
    private readonly buggies: HttpBuggyService,
    private readonly buggyOverviewService: BuggyOverviewService,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper,
    private readonly translate: TranslateService,
    private readonly printDocument: PrintDocument,
    private readonly navigationHelperService: NavigationHelperPrototypeService,
    private readonly removeConfirmation: RemoveConfirmationService
  ) {
    super();
  }

  public ngOnInit(): void {
    const colDefs = this.buggyOverviewService.getColumnDefs();

    this.hasPrintBuggyOverviewPermission = this.authentication.getCurrentSubscription()?.hasPermission(this.printBuggyOverviewPermission) ?? false;

    this.config = this.crudOverviewDataBuilderFactory
      .getBuilder()
      .withExtraActions(
        this.hasPrintBuggyOverviewPermission
          ? [
              {
                title: this.translate.instant('PLANNING.BUGGY.PRINT_BUGGY_REPORT'),
                onlySingleSelection: true,
                closeMenuOnClick: true,
                onClick: (grid: AgGridAngular): void => this.printOverviewForSelectedBuggy(grid)
              }
            ]
          : []
      )
      .withDefaultButtons({
        crudOverviewButtonConfigs: this.gridActionButtonsHelper.getActionButtonsForGridSelection({
          requiredPermissionToEdit: RouteUtils.paths.utilities.buggy.editBuggy.requiredPermission,
          withEdit: (grid: GridButtonClickedEvent) => this.navigateToGivenRoute(grid, 'edit'),
          withDuplicate: (grid: GridButtonClickedEvent) => this.navigateToGivenRoute(grid, 'duplicate'),
          withDelete: (grid: GridButtonClickedEvent) => this.delete(grid),
          allowDeleteMultiple: false
        } as CrudActions)
      })
      .withCrudGridOptions(colDefs, GridIdentifier.BUGGY, null, (builder: GridOptionsBuilder) => this.buggyOverviewService.getDefaultGridOptionsBuilder(builder).withRowSelection(false).build())
      .withRowData(this.buggies.getAll())
      .withNewClicked(() => this.navigationHelperService.navigateWithRoute({route: this.pageUrls.add}))
      .withEntityName('PLANNING.BUGGY.BUGGY')
      .withCustomSettingsClicked(() => this.openCustomSettings())
      .build();
  }

  private navigateToGivenRoute({gridApi, rowNode}: GridButtonClickedEvent, action: string): void {
    this.navigationHelperService.navigateWithRoute({
      route: this.pageUrls[action].replace(':id', AssertionUtils.isNullOrUndefined(rowNode) ? gridApi?.getSelectedRows()[0].id : rowNode.id)
    });
  }

  private delete({gridApi, rowNode}: GridButtonClickedEvent): void {
    const data = AssertionUtils.isNullOrUndefined(rowNode) ? gridApi?.getSelectedRows()[0] : rowNode.data;
    this.removeConfirmation.open(data, this.buggies.delete(data.id), 'PLANNING.BUGGY.BUGGY', gridApi);
  }

  public printOverviewForSelectedBuggy(grid: AgGridAngular): void {
    if (!this.isPrintingBuggyReport && this.hasPrintBuggyOverviewPermission) {
      const selectedBuggy = grid?.api?.getSelectedRows()[0];

      if (!AssertionUtils.isNullOrUndefined(selectedBuggy) && selectedBuggy instanceof BuggyForProductionSchedule) {
        this.isPrintingBuggyReport = true;
        forkJoin([this.buggies.getBuggyReportXML(Number(selectedBuggy.id)), this.buggies.getBuggyReportXSLT()])
          .pipe(
            takeUntil(this.unSubscribeOnViewDestroy),
            finalize(() => (this.isPrintingBuggyReport = false))
          )
          .subscribe(([xml, xslt]: [string, string]) => {
            this.printDocument.execute([xml, xslt]);
          });
      }
    }
  }

  private openCustomSettings(): void {
    this.navigationHelperService.navigateWithContentSwitcher({
      componentInfo: {
        component: BuggyCustomSettingsComponent
      }
    });
  }
}
